<template>
  <section id="opinie" class="reviews section">
    <div class="container">
      <div class="section-header" data-aos="fade-up">
        <h2>Opinie Klientów</h2>
        <div class="section-line"></div>
        <p>Poznaj opinie naszych klientów i podziel się swoimi doświadczeniami ze współpracy z VI-GAS</p>
      </div>
      
      <div class="reviews-grid">
        <div v-for="review in reviews" :key="review.id" class="review-card" data-aos="fade-up">
          <div class="stars">
            <span v-for="n in review.rating" :key="n" class="star">⭐</span>
          </div>
          <p class="review-text">"{{ review.comment }}"</p>
          <div class="review-author">
            <h4>{{ review.name }}</h4>
            <span class="date">{{ formatDate(review.created_at) }}</span>
          </div>
        </div>
      </div>
      
      <div class="add-review" data-aos="fade-up">
        <h3>Dodaj swoją opinię</h3>
        <form @submit.prevent="submitReview" class="review-form">
          <div class="form-group">
            <label for="reviewName">Imię</label>
            <input type="text" id="reviewName" v-model="newReview.name" required>
          </div>
          
          <div class="form-group">
            <label>Ocena</label>
            <div class="rating-input">
              <button 
                v-for="n in 5" 
                :key="n"
                type="button"
                class="star-btn"
                :class="{ active: n <= newReview.rating }"
                @click="setRating(n)"
              >
                ⭐
              </button>
            </div>
          </div>
          
          <div class="form-group">
            <label for="reviewText">Twoja opinia</label>
            <textarea 
              id="reviewText" 
              v-model="newReview.text" 
              required
              rows="4"
              placeholder="Podziel się swoim doświadczeniem..."
            ></textarea>
          </div>
          
          <button type="submit" class="btn" :disabled="isSubmitting">
            <span v-if="isSubmitting">Wysyłanie...</span>
            <span v-else>Dodaj opinię</span>
          </button>
        </form>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, onMounted } from 'vue'
import { supabase } from '../services/supabase'

export default {
  name: 'ReviewsSection',
  setup() {
    const reviews = ref([])
    const isSubmitting = ref(false)
    
    const newReview = ref({
      name: '',
      rating: 5,
      text: ''
    })
    
    const setRating = (rating) => {
      newReview.value.rating = rating
    }
    
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: 'long'
      })
    }
    
    const loadReviews = async () => {
      try {
        const { data, error } = await supabase
          .from('reviews')
          .select('*')
          .eq('status', 'approved')
          .order('created_at', { ascending: false })
        
        if (error) throw error
        reviews.value = data
      } catch (error) {
        console.error('Error loading reviews:', error)
      }
    }
    
    const submitReview = async () => {
      isSubmitting.value = true
      
      try {
        const { error } = await supabase
          .from('reviews')
          .insert([
            {
              name: newReview.value.name,
              rating: newReview.value.rating,
              comment: newReview.value.text,
              status: 'pending',
              created_at: new Date().toISOString()
            }
          ])
        
        if (error) throw error
        
        alert('Dziękujemy za opinię! Po moderacji zostanie opublikowana.')
        
        // Reset form
        newReview.value = {
          name: '',
          rating: 5,
          text: ''
        }
        
      } catch (error) {
        console.error('Error submitting review:', error)
        alert('Wystąpił błąd podczas wysyłania opinii.')
      } finally {
        isSubmitting.value = false
      }
    }
    
    onMounted(() => {
      loadReviews()
    })
    
    return {
      reviews,
      newReview,
      isSubmitting,
      setRating,
      submitReview,
      formatDate
    }
  }
}
</script>

<style lang="scss" scoped>
.reviews {
  background: #f8f9fa;
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
  
  h2 {
    font-size: 2.5rem;
    color: #333;
    margin-bottom: 15px;
  }
  
  .section-line {
    width: 60px;
    height: 4px;
    background: linear-gradient(135deg, #FF6B35, #FFD93D);
    margin: 0 auto 20px;
    border-radius: 2px;
  }
  
  p {
    font-size: 1.1rem;
    color: #666;
    max-width: 600px;
    margin: 0 auto;
  }
}

.reviews-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 60px;
}

.review-card {
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  
  .stars {
    margin-bottom: 15px;
    
    .star {
      font-size: 1.2rem;
    }
  }
  
  .review-text {
    font-size: 1.1rem;
    line-height: 1.6;
    color: #555;
    margin-bottom: 20px;
    font-style: italic;
  }
  
  .review-author {
    h4 {
      color: #333;
      margin-bottom: 5px;
    }
    
    .date {
      color: #999;
      font-size: 0.9rem;
    }
  }
}

.add-review {
  background: white;
  padding: 40px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  margin: 0 auto;
  
  h3 {
    text-align: center;
    margin-bottom: 30px;
    color: #333;
  }
}

.review-form {
  .form-group {
    margin-bottom: 25px;
    
    label {
      display: block;
      margin-bottom: 8px;
      font-weight: 600;
      color: #333;
    }
    
    input, textarea {
      width: 100%;
      padding: 12px 15px;
      border: 1px solid #ddd;
      border-radius: 10px;
      font-size: 1rem;
      transition: border-color 0.3s ease;
      
      &:focus {
        outline: none;
        border-color: #FF6B35;
      }
    }
    
    textarea {
      resize: vertical;
    }
  }
  
  .rating-input {
    display: flex;
    gap: 5px;
    
    .star-btn {
      background: none;
      border: none;
      font-size: 1.5rem;
      cursor: pointer;
      transition: transform 0.2s ease;
      opacity: 0.3;
      
      &.active {
        opacity: 1;
      }
      
      &:hover {
        transform: scale(1.1);
      }
    }
  }
  
  .btn {
    width: 100%;
    margin-top: 10px;
    
    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }
}

@media (max-width: 768px) {
  .reviews-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .add-review {
    padding: 30px 20px;
  }
  
  .section-header h2 {
    font-size: 2rem;
  }
}
</style>